import { useEffect, useState } from "react";
import { AlertTriangle, ArrowLeft, Download } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 });
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["Office Supplies", "Fuel", "Travel", "Marketing", "Software", "Payroll", "Professional Services", "Utilities", "Equipment", "Repairs", "Rent", "Insurance", "Taxes", "Other"];

export default function ReceivedInvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");

  const refresh = () => api.get(`/invoices/received/${id}`, { withCredentials: true }).then((r) => { setDoc(r.data); setNotes(r.data.notes || ""); }).catch((e) => setError(e.response?.data?.detail || "No se pudo cargar."));
  useEffect(() => { refresh(); }, [id]);

  const update = async (patch) => {
    try {
      await api.put(`/invoices/received/${id}`, patch, { withCredentials: true });
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos actualizar."); }
  };

  if (!doc) return <AppShell><main className="page"><p>{error || "Cargando..."}</p></main></AppShell>;
  const cfdi = doc.cfdi || {};

  return (
    <AppShell>
      <main className="page invoice-detail-page">
        <button className="detail-back" onClick={() => navigate("/facturas-recibidas")}><ArrowLeft size={16} />Facturas Recibidas</button>
        <header className="detail-head">
          <div>
            <small className="eyebrow">FACTURA RECIBIDA</small>
            <h1>{cfdi.supplier_name || "Sin proveedor"}</h1>
            <p>{cfdi.folio ? `Folio ${cfdi.folio} · ` : ""}{cfdi.invoice_date || ""}</p>
          </div>
          <div className="detail-head-actions">
            {doc.pdf_path && <a className="expenses-scan-button" href={`${BACKEND}/api/invoices/received/${doc.id}/pdf`} target="_blank" rel="noreferrer"><Download size={15} />PDF</a>}
            {doc.xml_path && <a className="expenses-scan-button" href={`${BACKEND}/api/invoices/received/${doc.id}/xml`} target="_blank" rel="noreferrer"><Download size={15} />XML</a>}
          </div>
        </header>

        {doc.issues?.length > 0 && (
          <div className="expenses-error" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={16} />
            <span>Alertas: {doc.issues.join(" · ")}</span>
          </div>
        )}

        <section className="detail-grid">
          <article>
            <b>Proveedor</b>
            <dl>
              <div><dt>Nombre</dt><dd>{cfdi.supplier_name || "—"}</dd></div>
              <div><dt>RFC</dt><dd>{cfdi.supplier_rfc || "—"}</dd></div>
              <div><dt>UUID</dt><dd style={{ fontSize: 11 }}>{cfdi.uuid || "—"}</dd></div>
              <div><dt>Folio</dt><dd>{cfdi.folio || "—"}</dd></div>
            </dl>
          </article>
          <article>
            <b>Pago</b>
            <dl>
              <div><dt>Método</dt><dd>{cfdi.payment_method || "—"}</dd></div>
              <div><dt>Forma</dt><dd>{cfdi.payment_form || "—"}</dd></div>
              <div><dt>Moneda</dt><dd>{cfdi.currency || "MXN"}</dd></div>
              <div><dt>Versión</dt><dd>{cfdi.cfdi_version || "4.0"}</dd></div>
              <div><dt>Tipo</dt><dd>{cfdi.cfdi_type || "—"}</dd></div>
              <div><dt>Fecha</dt><dd>{cfdi.invoice_date || "—"}</dd></div>
            </dl>
          </article>
          <article>
            <b>Clasificación</b>
            <dl>
              <div><dt>Categoría</dt><dd>
                <select value={doc.category} onChange={(e) => update({ category: e.target.value })} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid var(--line)" }}>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </dd></div>
              <div><dt>Estado</dt><dd>
                <select value={doc.status} onChange={(e) => update({ status: e.target.value })} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid var(--line)" }}>
                  <option value="pending">Pendiente</option>
                  <option value="paid">Pagada</option>
                  <option value="deductible">Deducible</option>
                  <option value="cancelled">Cancelada</option>
                </select>
              </dd></div>
              <div><dt>Sugerida IA</dt><dd>{doc.suggested_category || "—"}</dd></div>
            </dl>
          </article>
          <article className="detail-items">
            <b>Conceptos</b>
            <table>
              <thead><tr><th>Descripción</th><th>Cantidad</th><th>Precio</th><th>Importe</th></tr></thead>
              <tbody>
                {(cfdi.items || []).map((it, i) => (
                  <tr key={i}><td>{it.description}</td><td>{it.quantity}</td><td>{money.format(it.unit_price)}</td><td>{money.format(it.total)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="detail-totals">
              <div><span>Subtotal</span><b>{money.format(cfdi.subtotal || 0)}</b></div>
              <div><span>IVA</span><b>{money.format(cfdi.iva || 0)}</b></div>
              <div className="grand"><span>Total</span><b>{money.format(cfdi.total || 0)}</b></div>
            </div>
          </article>
          <article>
            <b>Notas</b>
            <textarea rows="4" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (doc.notes || "") && update({ notes })} style={{ width: "100%", padding: 10, border: "1px solid var(--line)", borderRadius: 8, fontFamily: "inherit" }} placeholder="Escribe una nota sobre esta factura..." />
          </article>
          {doc.ocr_text && (
            <article>
              <b>Texto OCR extraído</b>
              <pre style={{ fontSize: 11, maxHeight: 220, overflowY: "auto", background: "#fafbfc", padding: 10, borderRadius: 8, whiteSpace: "pre-wrap", margin: 0 }}>{doc.ocr_text.slice(0, 3000)}</pre>
            </article>
          )}
        </section>
      </main>
    </AppShell>
  );
}
